import React from 'react';
import Styles from './PaneHandle.module.css';

export type PaneHandleDirection = 'left' | 'right' | 'up' | 'down';

interface Props {
  /** The way the pane moves when clicked - the arrow points this way on hover. */
  direction: PaneHandleDirection;
  label: string;
  onClick: () => void;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * A short line on a pane's edge that bends into an arrow on hover or keyboard focus, so
 * the control reads as a quiet divider at rest and only says which way it goes when the
 * pointer is on it. On a device that cannot hover the arrow shows all the time.
 *
 * A collapsed pane deliberately shows only the line too: collapse state is per browser, so
 * whoever sees a collapsed pane is whoever collapsed it.
 *
 * Drawn as two SVG strokes with round caps sharing one end point, so the caps overlap
 * exactly and it reads as one unbroken line, and as one arrow with a joined tip. Two HTML
 * boxes showed a sliver of gap between the halves at 100% scaling.
 */
export const PaneHandle = ({ direction, label, onClick, className = '', style }: Props) => {
  const vertical = direction === 'left' || direction === 'right';
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={style}
      className={`${Styles.handle} ${vertical ? Styles.vertical : Styles.horizontal} ${Styles[direction]} ${className}`}
    >
      {vertical ? (
        <svg width="16" height="48" viewBox="0 0 16 48" aria-hidden="true">
          <line className={Styles.a} x1="8" y1="16.5" x2="8" y2="24" />
          <line className={Styles.b} x1="8" y1="24" x2="8" y2="31.5" />
        </svg>
      ) : (
        <svg width="48" height="16" viewBox="0 0 48 16" aria-hidden="true">
          <line className={Styles.a} x1="16.5" y1="8" x2="24" y2="8" />
          <line className={Styles.b} x1="24" y1="8" x2="31.5" y2="8" />
        </svg>
      )}
    </button>
  );
};
